import type {
  ChannelDto,
  ContentAssetDto,
  ContentCollisionDto,
  ContentCommentDto,
  ContentItemDto,
  ContentVersionDto,
  ContentStatus,
  UserDto,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, query } from '../../lib/api.js';

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserDto[]>('/users'),
    staleTime: 5 * 60 * 1000,
  });
}

export interface ContentQueryParams {
  company?: string;
  areaId?: string;
  pillar?: string;
  status?: ContentStatus;
  channelId?: string;
  search?: string;
  overdueOnly?: boolean;
  activeOnly?: boolean;
  from?: string;
  to?: string;
  take?: number;
}

export function useContentList(params: ContentQueryParams) {
  return useQuery({
    queryKey: ['content', params],
    queryFn: () =>
      api.get<{ items: ContentItemDto[]; total: number }>(
        `/content${query({ ...params, take: params.take ?? 300 })}`,
      ),
  });
}

export function useContentItem(id: string | null) {
  return useQuery({
    queryKey: ['content-item', id],
    queryFn: () => api.get<ContentItemDto>(`/content/${id}`),
    enabled: Boolean(id),
  });
}

export function useContentVersions(id: string | null) {
  return useQuery({
    queryKey: ['content-versions', id],
    queryFn: () => api.get<ContentVersionDto[]>(`/content/${id}/versions`),
    enabled: Boolean(id),
  });
}

export function useComments(id: string | null) {
  return useQuery({
    queryKey: ['content-comments', id],
    queryFn: () => api.get<ContentCommentDto[]>(`/content/${id}/comments`),
    enabled: Boolean(id),
  });
}

export function useAddComment(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api.post<ContentCommentDto>(`/content/${id}/comments`, { body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['content-comments', id] });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useAddContributor(id: string) {
  return useContentMutation(
    (userId: string) => api.post<ContentItemDto>(`/content/${id}/contributors`, { userId }),
    id,
  );
}

export function useRemoveContributor(id: string) {
  return useContentMutation(
    (userId: string) => api.delete<ContentItemDto>(`/content/${id}/contributors/${userId}`),
    id,
  );
}

// ── Bilde-/grafikkbestillinger ────────────────────────────────────────────────

export function useAssets(id: string | null) {
  return useQuery({
    queryKey: ['content-assets', id],
    queryFn: () => api.get<ContentAssetDto[]>(`/content/${id}/assets`),
    enabled: Boolean(id),
  });
}

function useAssetMutation<TVariables>(id: string, fn: (variables: TVariables) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['content-assets', id] });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

export function useAddAsset(id: string) {
  return useAssetMutation(id, (brief: string) =>
    api.post<ContentAssetDto>(`/content/${id}/assets`, { brief }),
  );
}

export function useUpdateAsset(id: string) {
  return useAssetMutation(id, (input: { assetId: string; brief: string }) =>
    api.patch<ContentAssetDto>(`/content/${id}/assets/${input.assetId}`, { brief: input.brief }),
  );
}

export function useUploadAsset(id: string) {
  return useAssetMutation(id, (input: { assetId: string; file: File }) => {
    const body = new FormData();
    body.append('file', input.file);
    return api.postForm<ContentAssetDto>(`/content/${id}/assets/${input.assetId}/file`, body);
  });
}

export function useDeleteAsset(id: string) {
  return useAssetMutation(id, (assetId: string) => api.delete(`/content/${id}/assets/${assetId}`));
}

export function useChannels() {
  return useQuery({
    queryKey: ['channels'],
    queryFn: () => api.get<ChannelDto[]>('/channels'),
    staleTime: 5 * 60_000,
  });
}

/** Every mutation refreshes the plan, the dashboard and the item itself. */
function useContentMutation<TVariables>(
  fn: (variables: TVariables) => Promise<unknown>,
  contentId?: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['content'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
      if (contentId) {
        void queryClient.invalidateQueries({ queryKey: ['content-item', contentId] });
        void queryClient.invalidateQueries({ queryKey: ['content-versions', contentId] });
      }
    },
  });
}

export function useApproveContent(id: string) {
  return useContentMutation(() => api.post<ContentItemDto>(`/content/${id}/approve`), id);
}

export function useRejectContent(id: string) {
  return useContentMutation(
    (reason: string) => api.post<ContentItemDto>(`/content/${id}/reject`, { reason }),
    id,
  );
}

/** Ask Nova for a draft on this one item, whatever its status (07 — 2.1.5). */
export function useRequestDraft(id: string) {
  return useContentMutation(() => api.post<ContentItemDto>(`/content/${id}/request-draft`), id);
}

export function useWriteDraft(id: string) {
  return useContentMutation(
    (input?: { instruction?: string }) =>
      api.post<ContentItemDto>(`/content/${id}/write-draft`, input ?? {}),
    id,
  );
}

export function useMoveToDraft(id: string) {
  return useContentMutation(() => api.post<ContentItemDto>(`/content/${id}/to-draft`), id);
}

/**
 * One hook for every one-click board move. Taking the id at call time (not at
 * hook creation) keeps drag-and-drop from firing against a stale card id.
 */
export type BoardMoveAction = 'approve' | 'to-draft' | 'reopen';

export function useBoardMove() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: BoardMoveAction }) =>
      api.post<ContentItemDto>(`/content/${id}/${action}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['content'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

/**
 * Persist a manual within-column order on the board. Optimistic: the reordered
 * ids get ascending `boardOrder` values written into every cached content list
 * right away (the board sorts by it), then the real values are refetched on
 * settle. A different column is a status change — that stays with useBoardMove.
 */
export function useReorderContent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      status: _status,
      orderedIds,
    }: {
      status: ContentStatus;
      orderedIds: string[];
    }) => api.post<{ ok: true }>('/content/reorder', { status: _status, orderedIds }),
    onMutate: async ({ orderedIds }) => {
      await queryClient.cancelQueries({ queryKey: ['content'] });
      const order = new Map(orderedIds.map((cardId, index) => [cardId, index]));
      const snapshots = queryClient.getQueriesData<{ items: ContentItemDto[]; total: number }>({
        queryKey: ['content'],
      });
      for (const [key, value] of snapshots) {
        if (!value) continue;
        queryClient.setQueryData(key, {
          ...value,
          items: value.items.map((it) =>
            order.has(it.id) ? { ...it, boardOrder: order.get(it.id)! } : it,
          ),
        });
      }
      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      for (const [key, value] of context?.snapshots ?? []) {
        queryClient.setQueryData(key, value);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['content'] });
    },
  });
}

export function useScheduleContent(id: string) {
  return useContentMutation(
    (input: { publishAt: string; channelIds: string[]; override?: boolean }) =>
      api.post<ContentItemDto>(`/content/${id}/schedule`, input),
    id,
  );
}

export function useConfirmPublished(id: string) {
  return useContentMutation(
    (input: { externalRef?: string | null; channelTargetId?: string }) =>
      api.post<ContentItemDto>(`/content/${id}/confirm-published`, input),
    id,
  );
}

export function useCancelContent(id: string) {
  return useContentMutation(
    (reason?: string) => api.post<ContentItemDto>(`/content/${id}/cancel`, { reason }),
    id,
  );
}

export function useReopenContent(id: string) {
  return useContentMutation(() => api.post<ContentItemDto>(`/content/${id}/reopen`), id);
}

export function useRevertPublished(id: string) {
  return useContentMutation(
    (to: 'SCHEDULED' | 'DRAFT' | 'SUGGESTED') =>
      api.post<ContentItemDto>(`/content/${id}/revert-published`, {
        to,
        confirmedNotPublished: true,
      }),
    id,
  );
}

export function useDeleteContent(id: string) {
  return useContentMutation(() => api.delete(`/content/${id}`), id);
}

export function useUpdateContent(id: string) {
  return useContentMutation(
    (input: Record<string, unknown>) => api.patch<ContentItemDto>(`/content/${id}`, input),
    id,
  );
}

export function useCreateContent() {
  return useContentMutation((input: Record<string, unknown>) =>
    api.post<ContentItemDto>('/content', input),
  );
}

/**
 * Company-level, ±3-day clashes for a card — a soft heads-up, never blocking.
 * Keyed on the item's dates so it refreshes when the event/publish date changes.
 */
export function useCollisions(item: ContentItemDto | null | undefined) {
  return useQuery({
    queryKey: ['content-collisions', item?.id, item?.eventDate, item?.publishAt],
    queryFn: () =>
      api.get<{ collisions: ContentCollisionDto[] }>(`/content/${item!.id}/collisions`),
    enabled: Boolean(item && (item.eventDate || item.publishAt)),
  });
}

/** Ask Nova for title suggestions. Nothing is applied — the caller fills the field. */
export function useSuggestTitle(id: string) {
  return useMutation({
    mutationFn: () => api.post<{ suggestions: string[] }>(`/content/${id}/suggest-title`),
  });
}

/** Ask Nova to fill channel-adapted copy. `force` regenerates every channel. */
export function useSuggestChannelVariants(id: string) {
  return useContentMutation(
    (input?: { force?: boolean }) =>
      api.post<ContentItemDto>(`/content/${id}/suggest-channel-variants`, input ?? {}),
    id,
  );
}

export function useUpdateChannelTarget(contentId: string) {
  return useContentMutation(
    (input: { targetId: string; adaptedBody?: string; status?: string }) =>
      api.patch(`/content/${contentId}/targets/${input.targetId}`, {
        adaptedBody: input.adaptedBody,
        status: input.status,
      }),
    contentId,
  );
}
